import { ApplicationRoleCode } from '@kcftech/prometheus-client';
import { AccessContext } from 'contexts/AccessContext';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { RouteEntities } from 'contexts/RouteEntityContext';
import { IUserContext } from 'contexts/UserContext';
import {
    getCorporationDashboardBaseRoute,
    getCorporationLandingPageRoute,
    getCorporationReportsRoute,
    getCorporationSettingsRoute,
    getGlobalReportsRoute,
    getGlobalSettingsRoute,
    getHardwareRoute,
    getLocationDashboardBaseRoute,
    getLocationDeskBaseRoute,
    getLocationLandingPageRoute,
    getLocationReportsRoute,
    getLocationSettingsRoute,
    getLocationWorkbenchBaseRoute,
    getMapsRoute,
    getMissionControlRoute,
    isUserInternal,
} from 'utils/RouteHelper';

export const getMissionControlUrl = (
    { canViewMissionControl }: PermissionsContext,
    { corporation, location }: RouteEntities,
) => {
    if (!canViewMissionControl) {
        return undefined;
    }
    return getMissionControlRoute(corporation?.Id, location?.Id);
};

export const getLandingUrl = (
    { isPremium }: AccessContext,
    { corporation, location }: RouteEntities,
) => {
    // do not show landing icon if at a Basic corporation
    if (!isPremium && corporation !== undefined) {
        return undefined;
    }

    if (location) {
        return getLocationLandingPageRoute(location.Id);
    }
    if (corporation) {
        return getCorporationLandingPageRoute(corporation.Id);
    }
    return '/';
};

export const getDashboardUrl = (
    { isPremium }: AccessContext,
    { canViewLocationDashboards, canViewCorporationDashboards }: PermissionsContext,
    { corporation, location }: RouteEntities,
) => {
    if (!isPremium) {
        return undefined;
    }
    if (location && canViewLocationDashboards) {
        return getLocationDashboardBaseRoute(location.Id);
    }
    if (corporation && canViewCorporationDashboards) {
        return getCorporationDashboardBaseRoute(corporation.Id);
    }
    return undefined;
};

export const getDeskUrl = ({ isPremium }: AccessContext, { location }: RouteEntities) => {
    if (!isPremium) {
        return undefined;
    }
    if (location) {
        return getLocationDeskBaseRoute(location.Id);
    }
    return undefined;
};

export const getWorkbenchUrl = (
    { isPremium }: AccessContext,
    { canViewLocationWorkbench }: PermissionsContext,
    { location }: RouteEntities,
) => {
    if (!isPremium) {
        return undefined;
    }
    if (location && canViewLocationWorkbench) {
        return getLocationWorkbenchBaseRoute(location.Id);
    }
    return undefined;
};

export const getSettingsUrl = (
    { isPremium }: AccessContext,
    { user }: IUserContext,
    { canViewSettings }: PermissionsContext,
    { entityType, corporation, location }: RouteEntities,
) => {
    /**
     * Show the Settings module if:
     * The user can view the settings and the corporation is Premium
     * OR if you are a GlobalAdmin user always show
     * OR if there is no corporation selected and you are a KCF user (GlobalAdmin/Sales/Sentry)
     */
    if (
        (canViewSettings && isPremium) ||
        user?.ApplicationRole === ApplicationRoleCode.GlobalAdmin ||
        (corporation === undefined && isUserInternal(user?.ApplicationRole))
    ) {
        if (location) {
            return getLocationSettingsRoute(location.Id);
        }
        if (corporation) {
            return getCorporationSettingsRoute(corporation.Id);
        }
        if (entityType === 'none') {
            return getGlobalSettingsRoute();
        }
    }
    return undefined;
};

export const getReportsUrl = (
    { canViewReports, canViewGlobalReports }: PermissionsContext,
    { entityType, corporation, location }: RouteEntities,
) => {
    if (canViewReports) {
        if (location) {
            return getLocationReportsRoute(location.Id);
        }
        if (corporation) {
            return getCorporationReportsRoute(corporation.Id);
        }
        if (entityType === 'none' && canViewGlobalReports) {
            return getGlobalReportsRoute();
        }
    }
    return undefined;
};

export const getMapsUrl = (
    { isPremium }: AccessContext,
    { canViewCorporationMap }: PermissionsContext,
    { entityType, corporation, location, system }: RouteEntities,
) => {
    if (!isPremium) {
        return undefined;
    }
    if (entityType !== 'none' && corporation) {
        if (!canViewCorporationMap && !location && !system) {
            return undefined;
        }
        return getMapsRoute(corporation.Id, location?.Id, system?.Id);
    }
    return undefined;
};

export const getHardwareUrl = (
    { canViewHardware }: PermissionsContext,
    { corporation, location }: RouteEntities,
) => (canViewHardware ? getHardwareRoute(corporation?.Id, location?.Id, []) : undefined);
